import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

export type Locale = "en" | "ar"
export type Theme = "light" | "dark"

const LANGUAGE_KEY = "tat-language"
const THEME_KEY = "tat-theme"

export function detectLocale(languages: readonly string[] = navigator.languages): Locale {
  return languages.some((language) => language.toLowerCase().startsWith("ar"))
    ? "ar"
    : "en"
}

export function detectTheme(media = window.matchMedia("(prefers-color-scheme: dark)")): Theme {
  return media.matches ? "dark" : "light"
}

function readPreference<T extends string>(key: string, allowed: readonly T[]): T | null {
  try {
    const value = localStorage.getItem(key)
    return allowed.includes(value as T) ? (value as T) : null
  } catch {
    return null
  }
}

function writePreference(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // The in-memory preference still updates when storage is unavailable.
  }
}

interface PreferencesContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  theme: Theme
  setTheme: (theme: Theme) => void
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null)

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(
    () => readPreference(LANGUAGE_KEY, ["en", "ar"]) ?? detectLocale(),
  )
  const [theme, setThemeState] = useState<Theme>(
    () => readPreference(THEME_KEY, ["light", "dark"]) ?? detectTheme(),
  )

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr"
    document.documentElement.dataset.locale = locale
  }, [locale])

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document.documentElement.style.colorScheme = theme
  }, [theme])

  const value = useMemo<PreferencesContextValue>(
    () => ({
      locale,
      setLocale: (nextLocale) => {
        writePreference(LANGUAGE_KEY, nextLocale)
        setLocaleState(nextLocale)
      },
      theme,
      setTheme: (nextTheme) => {
        writePreference(THEME_KEY, nextTheme)
        setThemeState(nextTheme)
      },
    }),
    [locale, theme],
  )

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  )
}

export function usePreferences() {
  const context = useContext(PreferencesContext)

  if (!context) {
    throw new Error("usePreferences must be used within PreferencesProvider")
  }

  return context
}
